import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import clsx from 'clsx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'md' | 'sm';

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children: ReactNode;
  disabled?: boolean;
}

type ButtonAsButton = CommonProps & {
  as?: 'button';
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children' | 'disabled'>;

type ButtonAsAnchor = CommonProps & {
  as: 'a';
  href: string;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children' | 'href'>;

type ButtonAsLink = CommonProps & {
  as: 'link';
  to: LinkProps['to'];
} & Omit<LinkProps, 'className' | 'children' | 'to'>;

export type ButtonProps = ButtonAsButton | ButtonAsAnchor | ButtonAsLink;

const base =
  'inline-flex items-center justify-center text-center font-display font-medium select-none ' +
  'transition-[filter,border-color,color,box-shadow] duration-150 ease-out ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 ' +
  'rounded-md';

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-primary text-white shadow-[var(--shadow-panel)] hover:brightness-95',
  secondary:
    'bg-panel border border-hairline text-ink hover:border-ink',
  ghost:
    'bg-transparent text-ink underline-offset-4 hover:underline px-0',
};

const sizeClasses: Record<ButtonSize, string> = {
  md: 'h-11 px-6 text-[0.9375rem]',
  sm: 'h-9 px-4 text-[0.875rem]',
};

const ghostSizeOverride: Record<ButtonSize, string> = {
  md: 'h-11 text-[0.9375rem]',
  sm: 'h-9 text-[0.875rem]',
};

export const Button = forwardRef<HTMLElement, ButtonProps>(function Button(props, ref) {
  const {
    variant = 'primary',
    size = 'md',
    className,
    children,
    disabled,
  } = props;

  const classes = clsx(
    base,
    variant === 'ghost' ? ghostSizeOverride[size] : sizeClasses[size],
    variantClasses[variant],
    (disabled ?? false) && 'opacity-50 cursor-not-allowed pointer-events-none',
    className,
  );

  if ('as' in props && props.as === 'a') {
    const {
      as: _as,
      variant: _v,
      size: _s,
      className: _c,
      children: _ch,
      disabled: _d,
      ...rest
    } = props;
    void _as;
    void _v;
    void _s;
    void _c;
    void _ch;
    void _d;
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={classes}
        aria-disabled={disabled ? true : undefined}
        {...rest}
      >
        {children}
      </a>
    );
  }

  if ('as' in props && props.as === 'link') {
    const {
      as: _as,
      variant: _v,
      size: _s,
      className: _c,
      children: _ch,
      disabled: _d,
      to,
      ...rest
    } = props;
    void _as;
    void _v;
    void _s;
    void _c;
    void _ch;
    void _d;
    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        to={to}
        className={classes}
        aria-disabled={disabled ? true : undefined}
        {...rest}
      >
        {children}
      </Link>
    );
  }

  const {
    as: _as,
    variant: _v,
    size: _s,
    className: _c,
    children: _ch,
    disabled: _d,
    type,
    ...rest
  } = props as ButtonAsButton;
  void _as;
  void _v;
  void _s;
  void _c;
  void _ch;
  void _d;
  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type={type ?? 'button'}
      disabled={disabled}
      className={classes}
      {...rest}
    >
      {children}
    </button>
  );
});

Button.displayName = 'Button';
